import React from 'react';
import { Icon, Modal } from '@brighterion/br-ui';
import XLSX from 'xlsx';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { BULK_UPLOAD_BEGIN_TOKENIZING } from 'Utils';
import { DateService } from 'Services';
import ModalTitleFileNameAndSize from 'Components/ModalTitleFileNameAndSize';
import {
    POSSIBLE_STATUS,
    toggleConfirmModal,
    useUploaderContext,
    useUploaderDispatchContext,
    beginChunking
} from '../Uploader/context';

import styles from './ConfirmationModal.css';

type Props = {
    isPCIList: boolean;
};

type ConfirmationModalBodyProps = {
    rowCount: number;
    errorCount: number;
    addCount: number | null;
    totalChunks: number;
};

function ConfirmationModalBody({ rowCount, addCount, errorCount, totalChunks }: ConfirmationModalBodyProps) {
    const { t } = useTranslation();
    // need to do ternary so that 0 doesn't accidentally render to the UI
    const errorCountString = errorCount ? (
        <span className={clsx(styles.errorTextContainer, styles.errorText)}>
            <span className={clsx(styles.boldFont)}>
                {t('singleListBulkUpload.errorRowsFound', { count: errorCount })}
            </span>{' '}
            {t('singleListBulkUpload.willNotBeUploaded')}
        </span>
    ) : null;

    const errorListItem = errorCount ? (
        <li className={styles.errorText}>{t('singleListBulkUpload.ignoreCount', { count: errorCount })}</li>
    ) : null;

    return (
        <>
            <p>
                {t('singleListBulkUpload.rowCountPromptOne')}
                <span data-testid='bulk-row-count' className={styles.boldFont}>
                    {t('singleListBulkUpload.rowCount', { count: rowCount })}.{' '}
                </span>
                {errorCountString}
                {t('singleListBulkUpload.rowCountPromptTwo')}
            </p>
            {addCount == null ? null : (
                <ul className={styles.addCount}>
                    <li data-testid='add-count'>{t('singleListBulkUpload.addCount', { count: addCount })}</li>
                    <li data-testid='remove-count'>
                        {t('singleListBulkUpload.removeCount', {
                            count: rowCount - addCount - errorCount
                        })}
                    </li>
                    {errorListItem}
                </ul>
            )}
            <p>
                {t('singleListBulkUpload.chunkingText')}{' '}
                <span data-testid='bulk-chunk-count' className={styles.boldFont}>
                    {t('singleListBulkUpload.batchCount', { count: totalChunks })}.
                </span>{' '}
                {t('singleListBulkUpload.chunkingTextTwo')}
            </p>
            <p className={styles.boldFont}>{t('singleListBulkUpload.closeBrowserWarning')}</p>
        </>
    );
}

const ConfirmationModal = React.forwardRef(({ isPCIList }: Props, ref) => {
    const refCurrent = (ref as React.RefObject<HTMLIFrameElement>)?.current;
    const { fileDetails, confirmModalIsOpen, status } = useUploaderContext();
    const dispatch = useUploaderDispatchContext();
    const { t } = useTranslation();
    const isValidationError = Boolean(fileDetails.errorRows?.length);
    const disableUpload = status !== POSSIBLE_STATUS.processingFileComplete;

    const closeModal = () => {
        dispatch(toggleConfirmModal({ value: false, changeStatus: true }));
    };

    const modalTitleNode = isValidationError ? (
        <span className={styles.titleIconContainer}>
            <Icon size='xlg' name='error' className={styles.errorIcon} />
            {t('Your file contains errors')}
        </span>
    ) : (
        t('Confirm upload to List')
    );

    const modalFooterSubActions = isValidationError
        ? {
              onSubActionText: t('Export Errors as CSV'),
              onSubAction: () => {
                  const wb = XLSX.utils.book_new();
                  const ws = XLSX.utils.json_to_sheet(fileDetails.errorRows);
                  XLSX.utils.book_append_sheet(wb, ws, 'errors');
                  XLSX.writeFile(
                      wb,
                      `${fileDetails.fileName}-validation-errors-${DateService.getDateTime(
                          new Date(Date.now()).toUTCString()
                      )}.csv`
                  );
              }
          }
        : {};

    return (
        <Modal
            dataTestId='list-bulk-upload-confirm'
            containerClass={styles.modalContainer}
            isOpen={confirmModalIsOpen}
            closeOnClickOutside={false}
            closeModal={closeModal}
            status={isValidationError ? 'error' : undefined}
        >
            <Modal.Title
                className={styles.titleContainer}
                id='list-bulk-upload-confirm-title'
                // TODO: update br-ui modalTitle to accept a ReactNode as a title child
                // @ts-ignore
                title={modalTitleNode}
                onClose={closeModal}
            >
                <ModalTitleFileNameAndSize
                    hasError={isValidationError}
                    fileName={fileDetails.fileName}
                    fileSize={fileDetails.fileSize}
                />
            </Modal.Title>
            <div className={styles.content}>
                <ConfirmationModalBody
                    totalChunks={fileDetails.totalChunks}
                    addCount={fileDetails.addCount}
                    rowCount={fileDetails.totalNumberOfRows}
                    errorCount={Number(fileDetails.errorRows?.length)}
                />
            </div>
            <Modal.Footer
                id='list-bulk-upload-confirm-footer'
                onCloseText={t('Cancel')}
                onActionText={t('Upload')}
                disableOnAction={disableUpload}
                onClose={closeModal}
                onAction={() => {
                    if (isPCIList) {
                        refCurrent?.contentWindow?.postMessage(
                            {
                                type: BULK_UPLOAD_BEGIN_TOKENIZING
                            },
                            refCurrent.src
                        );
                    } else {
                        dispatch(beginChunking());
                    }
                }}
                {...modalFooterSubActions}
            />
        </Modal>
    );
});

export default ConfirmationModal;
